import { ApiLoading } from '../api/apiLoading';
import { ApiError } from '../api/apiError';
import { AuthState } from '../../auth/authState';
import { Config } from '../../featureFlags/config';
import { FeatureFlags } from '../../featureFlags/featureFlags';
import { Language, Message } from '../../localization/models';
import { LocaleState } from '../../localization/localeState';
import { TextDirection } from '../../localization/textDirection';
import { ParticipantProject, Requirement, Project, ProjectCSContact } from '../../project/models';
import { CustomResource, DevResource } from '../../project/resources/models';
import { Map } from '../../language/models';
import { Rater, RaterLevel, DirectoryRater, PreviousRater, EmailBody } from '../../project/survey/raters/models';
import { GoalAnswer } from '../../project/setGoals/models';
import { DevPlanItem, DevPlanAction, DevPlanFieldAndAnswer, StatusKey } from '../../project/devPlan/models';
import { Report, EQScores } from '../../project/viewReport/models';
import { RaterApprovalParticipant, RaterApprovalRater } from '../../raterApproval/models';
import {
  ReportRecipientProject,
  ReportRecipientParticipant,
  ReportRecipientReport,
  DashboardClient,
  DashboardAssessment,
  DashboardDates,
  DashboardDefaults,
} from '../../reportRecipient/models';
import { FeedbackSurveyQuestion, CompetencyAndQuestion } from '../../project/createSurvey/models';
import { PortalType } from '../../navigation/portalType';
import { DevPlanCollaborator, DevItemComment } from '../../project/devPlan/collaboration/models';
import { CollaboratorAuthInfo } from '../../project/devPlan/collaboration/currentCollaboratorState';
import { DevPlanFieldListItem } from '../../project/devPlan/listItems/models';
import { PreEnrollStatus } from '../../project/api/preEnrollECourseAction';


/**
 * Type definition for the global app state
 */
export interface AppState {
  // API State
  // -------------------------------------------------------------------------------------------------------------------
  readonly loading: Map<ApiLoading>;
  readonly error: Map<ApiError>;
  readonly lastActivity: Date;
  readonly sessionStorageLoaded: boolean;
  readonly showTimeoutModal: boolean;
  readonly showTimedOut: boolean;
  readonly timedOutHandle: number;

  // Configuration
  // -------------------------------------------------------------------------------------------------------------------
  readonly featureFlags: FeatureFlags | undefined;
  readonly config: Config | undefined;

  // App Localization
  // -------------------------------------------------------------------------------------------------------------------
  readonly locale: LocaleState;
  readonly allLanguages: Language[];

  // Auth
  // -------------------------------------------------------------------------------------------------------------------
  readonly auth: AuthState;
  readonly portalType?: PortalType;

  // Menu
  // -------------------------------------------------------------------------------------------------------------------
  openSideMenu: boolean;

  // Project
  // -------------------------------------------------------------------------------------------------------------------
  readonly displayIdMapping: Map<number>;
  readonly projects?: Project[];
  readonly projectSummary?: ParticipantProject;
  readonly participantRequirements?: Requirement[];
  readonly projectResources?: CustomResource[];
  readonly reports?: Report[];
  readonly devResources?: DevResource[];
  readonly reportData?: Uint8Array;
  readonly eBookUrl?: string;
  readonly eCourseUrl?: string;
  readonly eCoursePopup?: boolean;
  readonly eCourseworkUrl?: string;
  readonly eCourseworkPopup?: boolean;
  readonly eCourseDisplayName?: string;
  readonly reassessmentSurveyUrl?: string;

  readonly eCourse?: {
    preEnrollStatus?: PreEnrollStatus;
  };

  /**
   * Contact details (first name, last name, email) for project-designated customer support personnel
   *
   * Mapping: Project ID -> Contact Info Obj
   */
  readonly projectCSContacts: Map<ProjectCSContact>;

  /**
   * EQ scores from TalentSmart feedback reports
   *
   * Mapping: Release ID -> Scoresheet
   */
  readonly eqScores: Map<EQScores>;

  /**
   * EQ scores email templates are retrieved for preview in the `SendEQScoresModal`.
   * Here they're mapped to their respective (stringified) language Id.
   *
   * E.g. { '2': { html: ..., plainText: ..., extraTextToken: `'<@ExtraText>'` } }
   */
  readonly eqScoresEmailTemplates: Map<EmailBody>;

  // Project Localization
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Translations/messages specific to a project
   * {languageId: {key: value}}
   */
  readonly projectMsgs?: Map<Map<Message>>;

  /**
   * Allows lookup of the supported languages for a project
   *
   * E.g. { '12345': { ... englishName: 'Spanish', nativeName: 'Español', ... } }
   */
  readonly projectLanguages?: { readonly [projectId: string]: Language[]; };

  // Create Survey
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Collection of feedback survey questions
   */
  selectedQuestions?: Map<FeedbackSurveyQuestion[]>;

  /**
   * collection of competencies and questions
   */
  competenciesAndQuestions?: Map<CompetencyAndQuestion[]>;


  // Dev Plan
  // -------------------------------------------------------------------------------------------------------------------
  readonly devPlan?: {
    emailRemindersOn?: boolean;

    /**
     * The email address of the last person to which we sent:
     *
     * * a dev plan, if collaboration for the dev plan is disabled
     * * an invitation to collaborate, if collaboration for the dev plan is enabled
     */
    lastEmailRecipient?: string;

    items?: DevPlanItem[];

    /**
     * List of status names & IDs for dev plan item actions
     */
    actionStatuses?: StatusKey[];

    /**
     * Dev plan item ID -> the actions associated with that item
     */
    actionsByItemId?: Map<DevPlanAction[]>;

    /**
     * Dev plans have actions which in turn have questions the user must answer. These are those question/answer pairs.
     * Mapping: Dev plan item ID -> Action ID -> Field/Answer
     */
    fieldsAnswers?: Map<Map<DevPlanFieldAndAnswer[]>>

    /**
     * Dev actions have at most one competency item which is answered by selecting from a list of preexisting options,
     * rather than free-form text
     *
     * Mapping: Language ID -> List Item ID -> Field List Item
     */
    listItems?: Map<Map<DevPlanFieldListItem>>;

    /**
     * True if dev plan page has been visited
     */
    viewed?: boolean;

    // Dev Plan Collaboration
    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Information about the current collaborator. These are users who have been invited to interact with a
     * participant's dev plan, but don't necessary have the same access privileges as participants themselves.
     *
     * Note that there is no scenario in which this object and `auth.currentUser` should both be defined.
     */
    currentCollaborator?: CollaboratorAuthInfo;

    /**
     * Users who have been granted access to a project's dev plan (e.g., to comment on dev plan items)
     * Mapping: Project ID -> Collaborator
     */
    collaborators?: Map<DevPlanCollaborator[]>;

    /**
     * Comments left on dev items by collaborators
     * Mapping: Project ID -> Dev Item ID -> Comment
     */
    comments?: Map<Map<DevItemComment[]>>;
  };

  // Raters
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Invite email templates are retrieved for preview in the `CustomizeEmailModal`.
   * Here they're mapped to their respective project Id, then language Id (both stringified).
   *
   * E.g. { '111': { '2': { html: ..., plainText: ..., extraTextToken: `'<@ExtraText>'` } } }
   */
  readonly inviteEmailTemplates: Map<Map<EmailBody>>;

  /**
   * Approver email templates are retrieved for preview in the `CustomizeEmailModal`.
   * Here they're mapped to their respective project Id, then language Id (both stringified).
   *
   * E.g. { '111': { '2': { html: ..., plainText: ..., extraTextToken: `'<@ExtraText>'` } } }
   */
  readonly approverEmailTemplates: Map<Map<EmailBody>>;

  /**
   * Rater approval email templates are retrieved for preview in the `CustomizeEmailModal`.
   * Here they're mapped to their respective project Id, then language Id (both stringified).
   *
   * E.g. { '111': { '2': { html: ..., plainText: ..., extraTextToken: `'<@ExtraText>'` } } }
   */
  readonly raterApprovalEmailTemplates: Map<Map<EmailBody>>;

  /**
   * A collection of project participant raters mapped by project, then by individual rater Id.
   *
   * E.g. { '111': { '222': { ...Rater info... } } } represents rater 22, who has been invited to rate the user in
   * project 111
   **/
  readonly projectRaters?: Map<Map<Rater>>;

  /**
   * Collection of raters associated with previous projects, mapped by project Id, then by person Id.
   */
  readonly previousRaters?: Map<Map<PreviousRater>>;

  /**
   *  A collection of raters for a given project, returned by keyword search
   *
   *  Mapped by project Id, then by search term
   */
  readonly directoryRaters?: Map<Map<DirectoryRater[]>>;

  /**
   * A collection of the rater levels associated with a given project (mapped by project Id)
   */
  readonly projectRaterLevels?: Map<RaterLevel[]>;

  /**
   * Timestamp for the last attempt to send rater invitations; introduced as part of a debugging effort to troubleshoot
   * a hanging request issue.
   *
   * TODO - remove this once the hanging request issue is resolved
   */
  readonly lastInviteRequest?: Date;

  // Rater Approval
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Collection of participants whose raters the current user is assigned to approve
   */
  readonly raterApprovalParticipants?: RaterApprovalParticipant[];

  /**
   * Collection of raters the current user is assigned to approve
   *
   * Mapped by participant Id, then project Id
   */
  readonly raterApprovalRaters?: Map<Map<RaterApprovalRater[]>>;

  /**
   * A collection of email addresses returned as undeliverable by the SendGrid API
   */
  readonly undeliveredEmails?: string[];


  // Goals
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * A collection of goals and answers mapped by project, then by goal question id.
   *
   * E.g. { '111': { '222': { ...GoalAnswer info... } } } represents goal with question id 222
   * for project 111
   **/
  readonly goalsAnswers?: Map<Map<GoalAnswer>>;

  // Report Recipient
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Report recipient date range
   **/
  readonly reportRecipientDateRange: number;
  /**
   * Collection of projects with reports the recipient is assigned to receive
   **/
  readonly reportRecipientProjects?: ReportRecipientProject[];
  /**
   * Collection of participants with reports the recipient is assigned to receive
   **/
  readonly reportRecipientParticipants?: ReportRecipientParticipant[];
  /**
   * Collection of reports the the recipient is assigned to receive
   **/
  readonly reportRecipientReports?: ReportRecipientReport[];
  /**
   * Report recipient last reports only
   **/
  readonly reportRecipientLastOnly: boolean;
  /**
   * Collection of selected projects
   **/
  readonly reportRecipientSelectedProjects?: Map<ReportRecipientProject>;
  /**
   * Collection of selected participants
   **/
  readonly reportRecipientSelectedParticipants?: Map<ReportRecipientParticipant>;
  /**
   * Collection of dashboard clients
   **/
  readonly dashboardClients?: Map<DashboardClient>;
  /**
   * Collection of dashboard asssessments
   **/
  readonly dashboardAssessments?: Map<DashboardAssessment>;
  /**
   * Dashboard dates
   **/
  readonly dashboardDates?: DashboardDates;
  /**
   * Dashboard defaults
   **/
  readonly dashboardDefaults?: DashboardDefaults;  /**
   * True if we show a message on the login page
   **/
  readonly showLoginAnnouncement: boolean;
}


/**
 * Startup state for the app without e.g. user input
 */
export const makeInitialState = (
  lang: Language,
  onMobile: boolean,
): AppState => {
  const direction = lang.rtl ? TextDirection.RTL : TextDirection.LTR;

  return ({
    // API State
    loading: {},
    error:   {},
    lastActivity: new Date(),
    sessionStorageLoaded: false,
    showTimeoutModal: false,
    showTimedOut: false,
    timedOutHandle: 0,

    // Configuration
    featureFlags: undefined,
    config:       undefined,

    // I18n
    locale: {
      portalLanguages: {},
      lang:            lang,
      textDirection:   direction,
      translations:    {},
    },
    allLanguages:      [],

    // Side Menu
    openSideMenu: !onMobile,

    // Projects
    displayIdMapping:            {},
    projectCSContacts:           {},
    eqScores:                    {},
    inviteEmailTemplates:        {},
    approverEmailTemplates:      {},
    raterApprovalEmailTemplates: {},
    eqScoresEmailTemplates:      {},

    // Report Recipient
    reportRecipientDateRange: 3,
    reportRecipientLastOnly:  true,

    // Auth
    auth: {},

    // Show Login Announcement
    showLoginAnnouncement: false,
  });
};
